import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { GalleryImage } from 'Pages/vehicles/helpers/vehicle.schema';

import { useGalleryData } from '../Gallery.utils';

import { MARKER_SIZE, ORIGINAL_SIZES, SVG_OVERLAY_REGEXP } from './DamageImage.const';
import { getCoordinate, s3ToImgix } from './DamageImage.helpers';

import styles from './DamageImage.module.scss';

interface GalleryDamageLocationProps {
	data: NonNullable<GalleryImage['damageMeta']>;
}

const FALLBACK_SIZE = 1;

export const DamageImage: FC<GalleryDamageLocationProps> = ({ data }) => {
	const imgRef = useRef<HTMLImageElement>(null);
	const { bodyType } = useGalleryData();
	const [isReady, setIsReady] = useState(false);
	const [marker, setMarker] = useState({ height: 0, left: 0, top: 0, width: 0 });

	const overlayUrl = data.overlayUrl ?? '';
	const posX = data.x ?? 0;
	const posY = data.y ?? 0;
	const side = data.side ?? 'front';
	const size = data.size ?? 'small';

	const [ORIGINAL_WIDTH, ORIGINAL_HEIGHT] = ORIGINAL_SIZES[bodyType][side] ?? ORIGINAL_SIZES[bodyType].front ?? [600, 400];

	useEffect(() => {
		const [, originalWidth = ORIGINAL_WIDTH, originalHeight = ORIGINAL_HEIGHT] =
			(overlayUrl || '').match(SVG_OVERLAY_REGEXP) || [];
		if (isReady && data) {
			const width = imgRef.current?.width || FALLBACK_SIZE;
			const height = imgRef.current?.height || FALLBACK_SIZE;
			const markerSize = MARKER_SIZE[size] || 0;

			setMarker({
				height: markerSize,
				left: getCoordinate({ markerSize, originalSize: originalWidth, pos: posX, size: width }),
				top: getCoordinate({ markerSize, originalSize: originalHeight, pos: posY, size: height }),
				width: markerSize,
			});
		}
	}, [ORIGINAL_WIDTH, ORIGINAL_HEIGHT, data, imgRef, isReady, overlayUrl, posX, posY, size]);

	if (!overlayUrl) {
		return null;
	}

	return (
		<div className={styles.imageWrapper}>
			<div className={styles.image}>
				<img ref={imgRef} alt="" onLoad={() => setIsReady(true)} src={s3ToImgix(overlayUrl)} />
				<div className={styles.marker} data-testid="gallery-damage-location-marker" style={{ ...marker }} />
			</div>
		</div>
	);
};
